import { useNavigate, Link } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Wind, Trophy, Sparkles, Home, AlertTriangle, ClipboardList, Camera, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import InstallAppButton from '@/components/pwa/InstallAppButton';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_THRESHOLDS = [15, 20, 30];
const UNLOCK_COUNT = 7;
const UNLOCK_WINDOW_DAYS = 30;

const ExercisesHubPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const avatarFileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [showWarning, setShowWarning] = useState(() => {
    // Show only once per login session — use sessionStorage flag (cleared on login by AuthContext)
    try { return sessionStorage.getItem('safety_warning_seen') !== '1'; } catch { return true; }
  });
  const [boltRecent, setBoltRecent] = useState([]);

  // Client-side compression to 512×512 JPEG so the DB payload stays small.
  const shrinkImageToDataUri = (file) => new Promise((resolve, reject) => {
    const SIZE = 512;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = SIZE; canvas.height = SIZE;
      const ctx = canvas.getContext('2d');
      const scale = Math.max(SIZE / img.width, SIZE / img.height);
      const sw = SIZE / scale, sh = SIZE / scale;
      const sx = (img.width - sw) / 2, sy = (img.height - sh) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, SIZE, SIZE);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image invalide')); };
    img.src = url;
  });

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choisissez une image (JPG, PNG, HEIC…).'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop lourde (max 5 Mo).'); return; }
    setAvatarBusy(true);
    setAvatarMenuOpen(false);
    try {
      // Downscale + JPEG-compress the image client-side, then upload the
      // resulting blob as multipart/form-data. The backend stores it in
      // Emergent Object Storage (no more Base64 in Mongo).
      const dataUri = await shrinkImageToDataUri(file);
      const blob = await (await fetch(dataUri)).blob();
      const fd = new FormData();
      fd.append('file', blob, `avatar-${Date.now()}.jpg`);
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(`${API}/auth/avatar`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(data);
      toast.success('Photo mise à jour !');
    } catch (err) {
      toast.error('Impossible de sauvegarder : ' + (err.response?.data?.detail || err.message));
    } finally { setAvatarBusy(false); }
  };

  const handleAvatarRemove = async () => {
    setAvatarMenuOpen(false);
    if (!window.confirm('Retirer votre photo de profil ?')) return;
    setAvatarBusy(true);
    try {
      const token = localStorage.getItem('token');
      // Legacy JSON payload — the backend recognises `avatar_url: null` as
      // "clear the avatar" (works for both Base64 and Object Storage entries).
      const { data } = await axios.patch(`${API}/auth/avatar`, { avatar_url: null }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setUser(data);
      toast.success('Photo retirée.');
    } catch (err) {
      toast.error('Erreur : ' + (err.response?.data?.detail || err.message));
    } finally { setAvatarBusy(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/challenges?challenge_type=bolt&limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = Array.isArray(r.data) ? r.data : (r.data.challenges || []);
        const cutoff = Date.now() - UNLOCK_WINDOW_DAYS * 24 * 3600 * 1000;
        const recent = items
          .map((it) => {
            const raw = it.completed_at || it.created_at;
            const d = raw instanceof Date ? raw : new Date(raw);
            return { value: Number(it.duration_seconds) || 0, ts: d.getTime() };
          })
          .filter((it) => it.value > 0 && !isNaN(it.ts) && it.ts >= cutoff);
        setBoltRecent(recent);
      } catch { /* silent — badge simply hides */ }
    };
    load();
  }, []);

  // Compute how far along the user is toward the next unlock threshold.
  const oaProgress = useMemo(() => {
    let unlockedIdx = 0;
    for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
      const th = LEVEL_THRESHOLDS[i];
      const count = boltRecent.filter((h) => h.value >= th).length;
      if (count >= UNLOCK_COUNT) unlockedIdx = i + 1;
      else break;
    }
    const nextThreshold = LEVEL_THRESHOLDS[unlockedIdx] ?? null;
    if (nextThreshold == null) return { mastered: true, count: null, threshold: null, celebrating: false };
    const count = boltRecent.filter((h) => h.value >= nextThreshold).length;
    // Celebrate if the latest measurement (within 24h) was a successful hit toward the next threshold.
    const now = Date.now();
    const dayAgo = now - 24 * 3600 * 1000;
    const latest = boltRecent.reduce((best, h) => (best == null || h.ts > best.ts ? h : best), null);
    const celebrating = !!(latest && latest.ts >= dayAgo && latest.value >= nextThreshold);
    return { mastered: false, count, threshold: nextThreshold, celebrating };
  }, [boltRecent]);

  const closeWarning = () => {
    setShowWarning(false);
    try { sessionStorage.setItem('safety_warning_seen', '1'); } catch { /* ignore */ }
  };

  const exercises = [
    {
      id: 'mon-programme',
      title: 'Mon Programme',
      subtitle: 'Le programme personnalisé réalisé par votre thérapeute',
      icon: ClipboardList,
      color: 'from-emerald-400 to-teal-500',
      shadowColor: 'shadow-emerald-300/50',
      glowColor: 'emerald',
      path: '/mon-espace/mon-programme?from=hub',
      delay: 0
    },
    {
      id: 'challenge',
      title: 'Défis Respiratoires',
      subtitle: 'Testez vos limites et battez vos records',
      icon: Trophy,
      color: 'from-amber-400 to-orange-500',
      shadowColor: 'shadow-amber-300/50',
      glowColor: 'amber',
      path: '/breathing/challenges-intro',
      delay: 0.25
    },
    {
      id: 'guided',
      title: 'Exercices Guidés',
      subtitle: 'Techniques respiratoires scientifiquement prouvées',
      icon: Wind,
      color: 'from-sky-400 to-cyan-500',
      shadowColor: 'shadow-sky-300/50',
      glowColor: 'sky',
      path: '/breathing/practice?mode=guided',
      delay: 0.5
    },
    {
      id: 'oxygen-advantage',
      title: 'Protocole Oxygen Advantage',
      subtitle: 'Programme quotidien pour l\'hyperventilation',
      logoSrc: '/oxygen-advantage-logo.webp',
      color: 'from-rose-400 to-red-500',
      shadowColor: 'shadow-rose-300/50',
      glowColor: 'rose',
      path: '/breathing/oxygen-advantage',
      delay: 1
    },
    {
      id: 'custom',
      title: 'Mes Exercices',
      subtitle: 'Vos créations personnalisées',
      icon: Sparkles,
      color: 'from-violet-400 to-purple-500',
      shadowColor: 'shadow-violet-300/50',
      glowColor: 'violet',
      path: '/breathing/practice?mode=custom',
      delay: 1.5
    }
  ];

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ fontFamily: 'Figtree, sans-serif' }}
    >
      {/* Background — same water image as the site home page, fixed, gently tinted */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            "url('https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/hrao4qok_ChatGPT%20Image%2027%20ao%C3%BBt%202026%2C%2008_49_20.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Soft veil for legibility over the water photo (matches CabinetHomePage) */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,247,252,0.55) 55%, rgba(214,235,246,0.55) 100%)' }}
      />
      {/* Hidden file input for avatar upload (Mon Programme circle) */}
      <input
        ref={avatarFileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { handleAvatarUpload(e.target.files?.[0]); e.target.value = ''; }}
        data-testid="hub-avatar-file-input"
      />
      {/* ===== Safety Warning Popup (glass) — shown once per session ===== */}
      {showWarning && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(6, 15, 30, 0.55)', backdropFilter: 'blur(4px)' }}
          onClick={closeWarning}
          data-testid="safety-warning-overlay"
        >
          <div
            className="relative w-full max-w-lg rounded-3xl p-6 sm:p-8 text-white"
            style={{
              background: 'rgba(255,255,255,0.09)',
              backdropFilter: 'blur(24px) saturate(1.3)',
              WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
              border: '1px solid rgba(255,255,255,0.22)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 30px 80px -20px rgba(0,0,0,0.55)',
            }}
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-labelledby="safety-warning-title"
            data-testid="safety-warning-dialog"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #FBBF24 0%, #F97316 100%)',
                  boxShadow: '0 0 24px rgba(251,146,60,0.4)',
                }}
              >
                <AlertTriangle className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h2
                id="safety-warning-title"
                className="text-xl sm:text-2xl font-semibold"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Attention
              </h2>
            </div>
            <div className="space-y-3 text-sm sm:text-[15px] leading-relaxed text-white/90">
              <p>
                Le travail sur la respiration est une pratique puissante qui a des effets sur le cœur, la circulation, le débit sanguin et donc la tension artérielle, ainsi que sur le système nerveux.
              </p>
              <p>
                Avant de commencer toute pratique respiratoire intense, consultez impérativement votre médecin, et assurez-vous d&apos;avoir appris correctement les techniques auprès d&apos;un professionnel qualifié.
              </p>
              <p>
                N&apos;oubliez pas&nbsp;: le corps est une machine qui répond positivement à la répétition en douceur, en progression guidée par une capacité d&apos;écoute et du respect de la physiologie.
              </p>
              <p className="font-medium" style={{ color: '#FBBF24' }}>
                Tout ce qui est «&nbsp;Trop, Trop Vite, Trop Fort&nbsp;» est contre-productif et potentiellement dangereux pour la santé…
              </p>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                onClick={closeWarning}
                className="px-10 py-3 rounded-full font-semibold text-white transition-all duration-300 hover:scale-[1.03]"
                style={{
                  background: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 100%)',
                  boxShadow: '0 0 24px rgba(14,165,233,0.55), 0 10px 24px rgba(0,0,0,0.25)',
                }}
                data-testid="safety-warning-ok-btn"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 -top-48 -left-48 bg-sky-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute w-96 h-96 -bottom-48 -right-48 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute w-64 h-64 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Breathing animation keyframes — scale only (no vertical float), matches /respiration */}
      <style>{`
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.09);
          }
        }
        
        @keyframes breatheShadow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(var(--glow-color), 0.3),
                        0 0 80px rgba(var(--glow-color), 0.1);
          }
          50% {
            box-shadow: 0 0 60px rgba(var(--glow-color), 0.5),
                        0 0 120px rgba(var(--glow-color), 0.2);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .breathing-circle {
          animation: breathe 5s ease-in-out infinite;
        }

        .breathing-circle-sky {
          --glow-color: 56, 189, 248;
          animation: breathe 5s ease-in-out infinite, breatheShadow 5s ease-in-out infinite;
        }
        
        .breathing-circle-amber {
          --glow-color: 251, 191, 36;
          animation: breathe 5s ease-in-out infinite, breatheShadow 5s ease-in-out infinite;
          animation-delay: 0.6s;
        }
        
        .breathing-circle-violet {
          --glow-color: 167, 139, 250;
          animation: breathe 5s ease-in-out infinite, breatheShadow 5s ease-in-out infinite;
          animation-delay: 1.2s;
        }

        /* OA celebration animations (24h after a successful measurement) */
        @keyframes oaBadgePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(56,189,248,0.55); }
          50% { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(56,189,248,0); }
        }
        .oa-badge-pulse {
          animation: oaBadgePulse 1.8s ease-in-out infinite;
        }
        @keyframes oaCelebrationRing {
          0% { box-shadow: 0 0 0 0 rgba(52,211,153,0.55); opacity: 0.9; }
          70% { box-shadow: 0 0 0 24px rgba(52,211,153,0); opacity: 0; }
          100% { box-shadow: 0 0 0 0 rgba(52,211,153,0); opacity: 0; }
        }
        .oa-celebration-ring {
          animation: oaCelebrationRing 2s ease-out infinite;
          border: 2px solid rgba(52,211,153,0.6);
        }
      `}</style>

      {/* Header */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-3">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 font-medium transition-colors"
        >
          <Home size={20} />
          Retour à l&apos;accueil
        </Link>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-2 pb-40 sm:pb-24">
        {/* Install-as-PWA prompt (mobile only, sticky above the nav bar) */}
        <InstallAppButton />

        {/* Title */}
        <div className="text-center mb-12">
          {/* Brand mark — centered above the title, size scales with viewport */}
          <img
            src="/logo-mains-nuages.png"
            alt="Physiothérapie Les Mains Nuages"
            className="mx-auto mb-4 h-14 sm:h-20 md:h-24 lg:h-28 w-auto object-contain"
            style={{ filter: 'drop-shadow(0 4px 14px rgba(15,32,64,0.18))' }}
            data-testid="hub-brand-horizontal-logo"
          />
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-light text-slate-900 mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Respiration <span className="text-sky-600 font-normal">Santé</span>
          </h1>
          <p className="text-lg text-slate-700 max-w-xl mx-auto">
            Choisissez votre pratique et commencez votre voyage vers une meilleure santé respiratoire
          </p>
        </div>

        {/* Circles Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-5xl mx-auto">
          {exercises.map((exercise) => (
            <div
              key={exercise.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(exercise.path)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(exercise.path); } }}
              className="group flex flex-col items-center focus:outline-none cursor-pointer"
              data-testid={`exercise-hub-${exercise.id}`}
            >
              {/* Circle */}
              <div 
                className={`
                  relative w-48 h-48 md:w-52 md:h-52 lg:w-60 lg:h-60 
                  rounded-full 
                  flex items-center justify-center
                  cursor-pointer
                  transition-all duration-500
                  group-hover:scale-110
                  breathing-circle-${exercise.glowColor}
                `}
                style={{ 
                  animationDelay: `${exercise.delay}s`,
                  background: `linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)`,
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: `
                    0 8px 32px rgba(0,0,0,0.3),
                    inset 0 0 80px rgba(var(--glow-color), 0.3)
                  `
                }}
              >
                {/* Gradient overlay for color — full opacity for vivid tone on light background */}
                <div 
                  className={`absolute inset-0 rounded-full bg-gradient-to-br ${exercise.color}`}
                />
                
                {/* Inner glow ring */}
                <div 
                  className="absolute inset-2 rounded-full"
                  style={{
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)'
                  }}
                />
                
                {/* Icon or logo image or avatar */}
                {exercise.id === 'mon-programme' && user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user?.name || 'Ma photo'}
                    className="absolute inset-0 z-10 w-full h-full rounded-full object-cover"
                    data-testid="hub-mon-programme-avatar"
                  />
                ) : exercise.logoSrc ? (
                  <div className="relative z-10 w-24 h-24 md:w-28 md:h-28 rounded-full bg-white/95 flex items-center justify-center p-3 shadow-lg transition-transform duration-300 group-hover:scale-110">
                    <img
                      src={exercise.logoSrc}
                      alt={exercise.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <exercise.icon
                    className="relative z-10 w-16 h-16 md:w-20 md:h-20 text-white drop-shadow-lg transition-transform duration-300 group-hover:scale-110"
                    strokeWidth={1.5}
                  />
                )}

                {/* Avatar upload menu on the Mon Programme circle (single discreet button) */}
                {exercise.id === 'mon-programme' && (
                  <div
                    className="absolute -top-2 right-2 z-30"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setAvatarMenuOpen((v) => !v); }}
                      aria-label="Options photo de profil"
                      title="Photo de profil"
                      className="w-7 h-7 rounded-full bg-white/85 hover:bg-white text-emerald-800 shadow-md border border-white/70 flex items-center justify-center transition backdrop-blur-sm"
                      data-testid="hub-avatar-menu-btn"
                    >
                      {avatarBusy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                    </button>
                    {avatarMenuOpen && (
                      <>
                        {/* click-away catcher */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={(e) => { e.stopPropagation(); setAvatarMenuOpen(false); }}
                          aria-hidden="true"
                        />
                        <div
                          className="absolute top-8 right-0 z-20 min-w-[168px] rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden"
                          data-testid="hub-avatar-menu"
                        >
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); avatarFileRef.current?.click(); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-800 hover:bg-emerald-50 transition text-left"
                            data-testid="hub-avatar-change"
                          >
                            <Camera size={14} className="text-emerald-700" />
                            {user?.avatar_url ? 'Changer la photo' : 'Ajouter une photo'}
                          </button>
                          {user?.avatar_url && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleAvatarRemove(); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 transition text-left border-t border-slate-100"
                              data-testid="hub-avatar-remove"
                            >
                              <Trash2 size={14} />
                              Retirer la photo
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* OA progression pill (top-right corner) */}
                {exercise.id === 'oxygen-advantage' && (oaProgress.mastered || oaProgress.count != null) && (
                  <div
                    className={`absolute -top-1 -right-1 z-20 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-white shadow-lg ${oaProgress.celebrating && !oaProgress.mastered ? 'oa-badge-pulse' : ''}`}
                    style={{
                      background: oaProgress.mastered
                        ? 'linear-gradient(135deg, #F59E0B, #B45309)'
                        : 'linear-gradient(135deg, #0EA5E9, #0284C7)',
                      border: '2px solid rgba(255,255,255,0.9)',
                    }}
                    data-testid="oa-hub-badge"
                    aria-label={oaProgress.mastered ? 'Maître' : `${oaProgress.count} sur 7 mesures ≥ ${oaProgress.threshold} secondes`}
                  >
                    {oaProgress.mastered
                      ? '🏆 Maître'
                      : <>{oaProgress.count}/{UNLOCK_COUNT} <span className="opacity-80 font-medium">≥ {oaProgress.threshold}s</span></>
                    }
                  </div>
                )}

                {/* Celebration ring on tile when the latest BOLT was a success (24h) */}
                {exercise.id === 'oxygen-advantage' && oaProgress.celebrating && !oaProgress.mastered && (
                  <span className="oa-celebration-ring absolute inset-0 rounded-full pointer-events-none" aria-hidden="true" />
                )}
                
                {/* Pulse ring on hover */}
                <div className="absolute inset-0 rounded-full border-2 border-white/30 opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
              </div>

              {/* Title */}
              <h2 
                className="mt-6 text-xl md:text-2xl font-medium text-slate-900 text-center transition-colors group-hover:text-sky-600"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {exercise.title}
              </h2>
              
              {/* Subtitle */}
              <p className="mt-2 text-sm text-slate-600 text-center max-w-[200px] group-hover:text-slate-800 transition-colors">
                {exercise.subtitle}
              </p>
            </div>
          ))}
        </div>

        {/* Philosophy quote */}
        <div className="mt-20 text-center">
          <p className="text-slate-600 italic text-sm max-w-2xl mx-auto">
            &quot;Comprendre la santé comme l&apos;équilibre parfait entre tension et relâchement, 
            restaurer l&apos;harmonie par la compréhension, l&apos;écoute du corps, et l&apos;abandon des résistances&quot;
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExercisesHubPage;
